import { FC, useEffect, useState } from 'react';

import { Link } from '@remix-run/react';
import ResponsiveImage from '~/components/responsive-image';
import { Button } from '~/components/ui/button';
import { useRevalidateOnLotClose } from '~/hooks/use-revalidate-on-lot-close';
import { getTimedLotStatus } from '~/lib/lot-status';
import { formatCurrency, formatToK } from '~/lib/utils';
import { getHomepageTrendingProducts } from '~/services/api/products';

interface TrendingProductsProps {
  data: Awaited<ReturnType<typeof getHomepageTrendingProducts>>;
  heightAndWidth?: string;
}

const ProductItem: FC<{
  product: Awaited<ReturnType<typeof getHomepageTrendingProducts>>['list'][0];
  heightAndWidth: string;
}> = ({ product, heightAndWidth }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = new Date();
      setNow(currentTime);

      const refreshEnd = new Date(product.auctionLastCutOff ?? product.cutOffTime);
      if (currentTime >= refreshEnd) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [product.auctionLastCutOff, product.cutOffTime]);

  const auctionEnded = now >= new Date(product.cutOffTime);
  const timedLotStatus = getTimedLotStatus(
    {
      auctionType: product.auctionType,
      auctionStart: product.auctionStart,
      cutOffTime: product.cutOffTime,
      auctionLastCutOff: product.auctionLastCutOff,
      bidCount: product.bidCount,
      highestBidAmount: product.highestBidAmount,
      lotUnsold: product.lotUnsold,
    },
    now,
  );

  return (
    <Link to={`/product-detail/${product.slug}`} className="relative flex w-full basis-1/4 flex-col gap-3">
      <div className="flex flex-col items-start gap-1">
        <div className="relative w-full">
          {timedLotStatus ? (
            timedLotStatus.phase === 'closed' && timedLotStatus.outcome !== null ? (
              <img
                src={`/images/${timedLotStatus.outcome}.png`}
                alt={timedLotStatus.outcome}
                className="absolute -left-8 -top-5 z-10 h-20 w-24"
              />
            ) : null
          ) : !auctionEnded ? null : product.lotUnsold === false && product.bidCount > 0 ? (
            <img src={'/images/sold.png'} alt="sold" className="absolute -left-8 -top-5 z-10 h-20 w-24" />
          ) : (
            <img src={'/images/unsold.png'} alt="unsold" className="absolute -left-8 -top-5 z-10 h-20 w-24" />
          )}
          <ResponsiveImage
            src={product.imageUrl ?? '/images/placeholder-image-600x400.png'}
            alt={product.name}
            className={`mb-2 w-full object-cover ${heightAndWidth}`}
          />
        </div>
        <p className="font-semibold">{product.name}</p>
        <p className="text-xs font-normal text-gray-500">Lot: {product.lotNumber || 'N/A'}</p>
        {product.maximumPrice > 0 ? (
          <p className="text-xs font-normal text-gray-500">
            Est: {formatToK(product.minimumPrice)} - {formatToK(product.maximumPrice)}
          </p>
        ) : (
          <p className="text-xs font-normal text-gray-500">Est: {formatToK(product.minimumPrice)}</p>
        )}
        <p className="text-xs font-medium text-black">
          {formatCurrency(product.highestBidAmount)}{' '}
          <span className="text-xs font-normal text-slate-500">({product.bidCount} bids)</span>
        </p>
        {timedLotStatus?.phase === 'closed' && timedLotStatus.outcome === null ? (
          <p className="text-xs font-normal text-slate-500">Bidding closed</p>
        ) : null}
      </div>
    </Link>
  );
};

const TrendingProducts: FC<TrendingProductsProps> = ({ data }) => {
  // One timer for the row, not one per ProductItem.
  useRevalidateOnLotClose(data.list.map((product) => product.cutOffTime));

  return (
    <div className="flex flex-col gap-4 py-5">
      <p className="text-xl font-medium text-black">Trending Products</p>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-5">
        {data.list.map((product) => (
          <ProductItem key={product.id} product={product} heightAndWidth="h-60 w-60" />
        ))}
      </div>
      <Link className="my-10 flex justify-center" to="/products">
        <Button variant={'outline'}>Explore All Products</Button>
      </Link>
    </div>
  );
};

export default TrendingProducts;
